import React, { useRef, useState } from 'react'
import { default as NumberFormat } from 'react-number-format'
import styled from 'styled-components'
import Tooltip from '../../components/PlanList/Tooltip'

const StyledMultiStepper = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  box-shadow: 0 1px 1px 0 rgba(10, 22, 70, 0.1),
    0 0 1px 0 rgba(10, 22, 70, 0.06);
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: -40px;
  padding: 40px 24px;
  position: absolute;
  text-align: center;

  ${({ theme }) => theme.media.tablet} {
    margin: -120px auto 0;
    width: 80%;
  }

  ${({ theme }) => theme.media.desktop} {
    margin-top: -80px;
    min-width: 820px;
    width: unset;
  }
`

const StyledSteps = styled.div`
  display: inline-block;
  width: 300px;

  ${({ theme }) => theme.media.desktop} {
    min-width: 485px;
  }

  button {
    border-radius: 400px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.71;
    padding: 12px 24px;
    transition: 250ms;
  }

  button.submit {
    background-color: ${({ theme }) => theme.colors.red};
    color: ${({ theme }) => theme.colors.white};
  }

  button.back {
    background-color: #0000;
    color: ${({ theme }) => theme.colors.placeholderText};
    margin-right: 16px;
  }

  button.submit[disabled] {
    background-color: #e6a7a9;
    border-color: #e6a7a9;
  }

  input {
    align-items: baseline;
    display: flex;
    justify-content: center;
    text-align: center;
    border-radius: 20px;
    border: solid 1px #e1e4e8;
    color: #0a1f44;
    display: flex;
    font-size: 14px;
    line-height: 1.75;
    margin: 8px auto 56px;
    padding: 8px 12px;
    text-align: center;
    width: 50%;

    &:focus {
      border: 1px solid ${({ theme }) => theme.colors.placeholderText};
    }

    ::-webkit-input-placeholder {
      /* WebKit, Blink, Edge */
      color: ${({ theme }) => theme.colors.placeholderText};
    }

    :-moz-placeholder {
      /* Mozilla Firefox 4 to 18 */
      color: ${({ theme }) => theme.colors.placeholderText};
      opacity: 1;
    }

    ::-moz-placeholder {
      /* Mozilla Firefox 19+ */
      color: ${({ theme }) => theme.colors.placeholderText};
      opacity: 1;
    }

    :-ms-input-placeholder {
      /* Internet Explorer 10-11 */
      color: ${({ theme }) => theme.colors.placeholderText};
    }

    ::-ms-input-placeholder {
      /* Microsoft Edge */
      color: ${({ theme }) => theme.colors.placeholderText};
    }

    ::placeholder {
      /* Most modern browsers support this now. */
      color: ${({ theme }) => theme.colors.placeholderText};
    }
  }

  select {
    -webkit-appearance: none;
    -moz-appearance: none;
    background: transparent;
    background-image: url("data:image/svg+xml;utf8,<svg height='24' width='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'><path d='M9.567 3.261L5.99 6.935 2.412 3.26a.818.818 0 0 0-1.168.01.872.872 0 0 0-.01 1.2L5.4 8.75a.818.818 0 0 0 1.178 0l4.167-4.278a.871.871 0 0 0 .227-.832.845.845 0 0 0-.595-.611.819.819 0 0 0-.81.233z'/> /></svg>");
    background-repeat: no-repeat;
    background-position-x: 100%;
    background-position-y: 75%;
    border-radius: 20px;
    border: solid 1px #e1e4e8;
    color: #4e5d78;
    display: block;
    font-size: 14px;
    line-height: 1.75;
    margin: 8px auto 56px;
    padding: 8px 12px;
    text-align: center;
    width: 240px;

    &:focus {
      border: 1px solid ${({ theme }) => theme.colors.placeholderText};
    }
  }
`

const StyledStep = styled.div`
  color: #000;
  font-size: 14px;
  margin: 18px auto 36px;
  letter-spacing: 1px;
  text-align: center;
`

const StyledProgress = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 18px auto;
`

const StyledDot = styled.div`
  align-items: center;
  background-color: ${({ active }) => (active ? '#c02428' : '#c9ced6')};
  border-radius: 50%;
  display: flex;
  height: 16px;
  justify-content: center;
  position: relative;
  width: 16px;
  z-index: ${({ active }) => (active ? '1000' : '0')};

  ${({ theme }) => theme.media.desktop} {
    margin: 0 16px;
  }

  :first-child {
    z-index: 10;
  }

  ::before {
    background-color: ${({ active }) => (active ? '#c02428' : '#c9ced6')};
    content: '';
    height: 0.2em;
    left: -32px;
    position: absolute;
    top: 8px;
    width: 48px;
    z-index: 1;

    ${({ theme }) => theme.media.desktop} {
      left: -4em;
      width: 72px;
    }
  }

  :first-child::before {
    display: none;
  }
`

const StyledQuestion = styled.div`
  color: #000;
  font-size: 20px;
  font-weight: 500;
  min-width: 200px;
  padding: 32px 0;
  text-align: center;
`

const StyledInfo = styled.div`
  color: #4e5d78;
  font-size: 0.75em;
  margin: -40px auto;
  min-width: 200px;
  text-align: center;

  ${({ theme }) => theme.media.desktop} {
    max-width: 324px;
  }
`

const StyledButtons = styled.div`
  margin-top: 80px;
`

const steps = [
  {
    button: 'Get Started',
    id: 'emailQuantity',
    placeholder: '1,000,000',
    question: () => 'How many emails do you send each month?',
    step: 1,
    type: 'number',
    variant: null,
  },
  {
    button: 'Continue',
    id: 'emailProvider',
    question: () => 'Who is your current email provider?',
    step: 2,
    type: 'select',
    variant: null,
  },
  {
    button: 'Continue',
    id: 'deliveryRate',
    placeholder: '70%',
    question: () => 'What is your average delivery rate?',
    step: 3,
    type: 'number',
    variant: 'percent',
  },
  {
    button: 'Continue',
    id: 'averageOpenRate',
    info: 'Industry Average Open Rate: 17.8%',
    placeholder: '17.8%',
    question: () => 'What is your average open rate?',
    step: 4,
    type: 'number',
    variant: 'percent',
  },
  {
    button: 'Continue',
    id: 'averageClickThroughRate',
    info: 'Industry Average Click Through Rate: 2.6%',
    placeholder: '2.6%',
    question: () => 'What is your average click through rate?',
    step: 5,
    type: 'number',
    variant: 'percent',
  },
  {
    button: 'Continue',
    id: 'monthlyRevenue',
    placeholder: '$50,000',
    question: () => 'What is your monthly revenue from email?',
    step: 6,
    type: 'number',
    variant: 'dollar',
  },
  {
    button: 'See My Results',
    id: 'monthlyCost',
    placeholder: '$525',
    question: (emailProvider) => {
      if (emailProvider !== "In-House") {
        return `How much are you paying ${emailProvider} per month?`
      } else {
        return `How much does your in-house email solution cost to operate each month?`
      }
    },
    step: 7,
    type: 'number',
    variant: 'dollar',
  },
]

const costTable = [
  { maxQuantity: 99999, cost: 35 },
  { maxQuantity: 249999, cost: 80 },
  { maxQuantity: 499999, cost: 215 },
  { maxQuantity: 749999, cost: 400 },
  { maxQuantity: 999999, cost: 550 },
  { maxQuantity: 2499999, cost: 700 },
  { maxQuantity: Infinity, cost: 1250 },
]

const getCost = (quantity) => costTable.find((item) => quantity < item.maxQuantity).cost


const ROIMultiStepper = () => {
  const [currentIndex, setCurrentIndex] = useState(0)

  const [data, setData] = useState({
    emailQuantity: '',
    emailProvider: '',
    deliveryRate: '',
    averageOpenRate: '',
    averageClickThroughRate: '',
    monthlyRevenue: '',
    monthlyCost: '',
  })

  const inputRef = useRef()

  const handleKeyPress = (e) => {
    if (currentValue !== '' && currentValue !== undefined) {
      if (e.key === 'Enter') {
        if (currentIndex < steps.length - 1) {
          nextQuestion()
        } else {
          lastQuestion()
        }
      }
    }
  }

  const handleClick = () => {
    if ('click') {
      if (currentIndex < steps.length - 1) {
        nextQuestion()
      } else {
        lastQuestion()
      }
    }
  }

  const currentStep = steps[currentIndex]
  const currentValue = data[currentStep.id]
  const emailProvider = data.emailProvider

  const setCurrentValue = (value) => setData({ ...data, [currentStep.id]: value})

  const prevQuestion = () => {
    setCurrentIndex(currentIndex - 1)
    setCurrentValue('')
  }

  const nextQuestion = () => {
    if (currentIndex < steps.length - 1) {
      setCurrentIndex(currentIndex + 1)
    } 
  }

  const lastQuestion = () => {
    if (currentIndex === steps.length - 1) {
      const {
        averageClickThroughRate,
        averageOpenRate,
        deliveryRate,
        emailQuantity,
        monthlyRevenue,
      } = data

      // calculate delivery score
      const getDeliveryScore = (deliveryRate) => {
        if (deliveryRate >= 94) {
          return 'Excellent'
        } else if (deliveryRate <= 93 && deliveryRate  >= 89) {
          return 'Average'
        } else {
          return 'Below Average'
        }
      }
      const deliveryScore = getDeliveryScore(deliveryRate)

      // calculate lost revenue
      const lostOpens = emailQuantity * 97.4 * averageOpenRate - emailQuantity * deliveryRate * averageOpenRate
      const lostClicks = lostOpens * averageClickThroughRate
      const revPerClick = monthlyRevenue / (emailQuantity * deliveryRate * averageOpenRate * averageClickThroughRate)
      const lostRev = (lostClicks * revPerClick).toFixed(2).toLocaleString()

      const getLostRevenue = (lostRev) => {
        if (lostRev <= 0) {
          return 0
        } else {
          return parseFloat(lostRev).toLocaleString('en')
        }
      }

      const lostRevenue = getLostRevenue(lostRev)

      // calculate mg cost based on recommended plan price
      const mgCost = getCost(emailQuantity)

      // calculate potential revenue
      const potentialRev = (((monthlyRevenue - mgCost) / mgCost) * 100).toFixed()
      const potentialRevenue = parseFloat(potentialRev).toLocaleString('en')

      const callWebhook = async (data) => { 
        const url = 'https://hooks.zapier.com/hooks/catch/265526/ofqe6xy'
      
        return await fetch(url, {
          method: 'POST',
          mode: "no-cors",
          redirect:"follow",
          headers: {
            'Accept': 'application/json',
            'Access-Control-Allow-Origin': '*',
          },
          body: JSON.stringify({data}),
        })
        .then((result) => {window.location.href = `/email-marketing-roi-calculator-results/?delivery_score=${deliveryScore}&lost_revenue=${lostRevenue}&potential_revenue=${potentialRevenue}`})
      }

      callWebhook(data)
    }
  }

  return (
    <StyledMultiStepper>
      <StyledSteps>
        <StyledStep>Step {currentIndex + 1} / 7</StyledStep>

        <StyledProgress>
          {steps.map((item, index) => (
            <StyledDot key={`dot-${item.id}`} active={index <= currentIndex} />
          ))}
        </StyledProgress>

        <StyledQuestion>
          {currentStep.question(emailProvider)}


          {currentIndex === 2 && (
            <Tooltip>
              <p>Delivery rate is how many messages are delivered out of all messages sent in a given time period.</p>
            </Tooltip>
          )}
        
        </StyledQuestion>

        {currentStep.type === 'number' ? (
          <NumberFormat
            allowNegative={false}
            autoFocus={true}
            getInputRef={inputRef}
            onValueChange={(values) => setCurrentValue(values.floatValue)}
            placeholder={currentStep.placeholder}
            prefix={currentStep.variant === 'dollar' ? '$' : ''}
            required={true}
            suffix={currentStep.variant === 'percent' ? '%' : ''}
            thousandSeparator={true}
            value={currentValue}

            onKeyPress={handleKeyPress}

            isAllowed={values => {
              const { formattedValue, floatValue } = values
              if (currentStep.variant === 'percent') {
                if (floatValue > 100) {
                  return formattedValue === ''
                } else if (floatValue === 0) {
                  return formattedValue === ''
                } else {
                  return true
                }
              }

              if (currentStep.variant !== 'percent') {
                if (floatValue === 0 && floatValue !== ".") {
                  return formattedValue === ''
                } else {
                  return true
                }
              }
            }}
          />
        ) : (
          <select
            action="https://hooks.zapier.com/hooks/catch/265526/ofqe6xy/"
            autoFocus={true}
            onChange={(e) => setCurrentValue(e.target.value)}
            onKeyPress={handleKeyPress}
            ref={inputRef}
            value={currentValue}
          >
            <option value="">Please choose an option</option>
            <option value="Sendgrid">Sendgrid</option>
            <option value="Amazon SES">Amazon SES</option>
            <option value="Sparkpost">Sparkpost</option>
            <option value="Mailchimp">Mailchimp</option>
            <option value="your Email Service Provider">Other</option>
            <option value="In-House">I have an in-house solution</option>
          </select>
        )}

        <StyledInfo>{currentStep.info}</StyledInfo>

        <StyledButtons>
          {currentIndex !== 0 && (
            <button
              className="back"
              id="submitButton"
              onClick={prevQuestion}
              type="submit"
            >
              Back
            </button>
          )}
          <button
            className="submit"
            disabled={!currentValue}
            id="submitButton"
            onClick={handleClick}
            title="Please fill in input above to move on to next step."
            type="submit"
          >
            {currentStep.button}
          </button>
        </StyledButtons>
      </StyledSteps>
    </StyledMultiStepper>
  )
}

export default ROIMultiStepper